import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import DialogComponent from '../../../component/dialog/dialog.component';
import LinechartOfBusinessData, {
    createDashboardBusinessLineApexOptions,
} from '../../../component/charts/line-chart-of-business-data';
import PieChartOfBusinessData from '../../../component/charts/pie-chart-of-business-data';
import i18n from '../../../i18n';
import { getPieChartColorsByValues, sortPieChartParallelByValueDesc } from '../../../themes/tokens/data-chart-tokens';

/** 图表标签颜色。 */
const CHART_LABEL_COLOR = 'var(--text-neutral-500)';

/** 对比曲线点。 */
interface SalesCompareCurvePoint {
    /** 横轴日期（YYYY-MM-DD）。 */
    x: string;
    /** 纵轴值（数量）。 */
    y: number;
}

/** 对比项。 */
export interface SalesCompareDialogItem {
    /** 销售金额。 */
    total_amount: number;
    /** 销售数量，用于销售量占比饼图。 */
    total_quantity: number;
    /** 稳定 key。 */
    key: string;
    /** 展示名称。 */
    label: string;
    /** 对比金额（格式化文案，用于销售额饼图图例）。 */
    amount: string;
    /** 对比数量（格式化文案，用于销售量饼图图例）。 */
    quantity: string;
    /** 销售占比（百分比数字）。 */
    ratio: number;
    /** 数量趋势线。 */
    curve: SalesCompareCurvePoint[];
}

/** 组件属性。 */
interface SalesCompareDialogProps {
    /** 弹窗开关。 */
    open: boolean;
    /** 关闭弹窗。 */
    onClose: () => void;
    /** 折线+饼图对比项。 */
    items: SalesCompareDialogItem[];
    /** 弹窗标题。 */
    title: string;
    /** 折线图标题。 */
    lineChartTitle: string;
}

/**
 * 按日期对齐多条趋势线，避免接口返回缺点位时错位。
 * @param items - 当前选中的对比项
 * @returns 对齐后的横轴与序列
 */
function buildAlignedTrend(items: SalesCompareDialogItem[]) {
    /** 所有出现过的日期点。 */
    const categories: string[] = [];
    /** 用于去重的日期集合。 */
    const categorySet = new Set<string>();

    items.forEach((item) => {
        item.curve.forEach((point) => {
            if (!categorySet.has(point.x)) {
                categorySet.add(point.x);
                categories.push(point.x);
            }
        });
    });

    categories.sort((left, right) => {
        /** 左侧日期对象。 */
        const leftMoment = moment(left);
        /** 右侧日期对象。 */
        const rightMoment = moment(right);
        if (leftMoment.isValid() && rightMoment.isValid()) {
            return leftMoment.valueOf() - rightMoment.valueOf();
        }
        return left.localeCompare(right);
    });

    /** 对齐后的序列列表。 */
    const series = items.map((item) => {
        /** 当前项日期到数量映射。 */
        const pointMap = new Map(item.curve.map((point) => [point.x, point.y]));
        return {
            name: item.label,
            data: categories.map((category) => pointMap.get(category) ?? 0),
        };
    });

    return {
        categories,
        series,
    };
}

/**
 * 渲染单个占比饼图区块（饼图 + 外侧图例）。
 * @param title - 区块标题
 * @param series - 饼图数值序列
 * @param labels - 饼图标签
 * @param legendValues - 图例次要数值文案
 * @param renderEmpty - 空态渲染函数
 * @returns 饼图区块 JSX
 */
function ComparePieSection({
    title,
    series,
    labels,
    legendValues,
    renderEmpty,
}: {
    title: string;
    series: number[];
    labels: string[];
    legendValues: string[];
    renderEmpty: () => React.ReactNode;
}) {
    /** 按 value 从大到小排序后的饼图数据（扇区序与 App 一致）。 */
    const sortedPie = useMemo(
        () => sortPieChartParallelByValueDesc(series, labels, [legendValues]),
        [labels, legendValues, series],
    );
    /** 排序后的扇区数值。 */
    const sortedSeries = sortedPie.values;
    /** 排序后的扇区标签。 */
    const sortedLabels = sortedPie.labels;
    /** 排序后的图例数值文案。 */
    const sortedLegendValues = sortedPie.parallel[0] ?? [];
    /** 饼图扇区色（与外侧图例一致）。 */
    const pieColors = useMemo(
        () => getPieChartColorsByValues(sortedSeries, sortedLabels),
        [sortedLabels, sortedSeries],
    );
    /** 饼图是否存在有效占比数据。 */
    const hasPieData = sortedSeries.some((value) => value > 0);
    /** 饼图图例项。 */
    const pieLegendItems = sortedLabels.map((label, index) => ({
        /** 图例名称。 */
        label,
        /** 图例数值文案。 */
        valueText: sortedLegendValues[index] ?? '',
        /** 图例颜色（与饼图扇区一致）。 */
        color: pieColors[index % pieColors.length],
    }));

    return (
        <Stack spacing={2}>
            <Typography sx={{ color: 'var(--text-dark)' }}>
                {title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Box sx={{ flex: '0 0 280px', width: 280, height: 280 }}>
                    {hasPieData ? (
                        <PieChartOfBusinessData
                            data={sortedSeries}
                            labels={sortedLabels}
                            titleText=""
                            layout="dialog"
                            colors={pieColors}
                            height={280}
                        />
                    ) : (
                        renderEmpty()
                    )}
                </Box>
                <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                    {pieLegendItems.map((item, index) => (
                        <Box key={`${item.label}-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: item.color, mt: '5px', flexShrink: 0 }} />
                            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                                <Typography sx={{ color: 'var(--text-dark)' }}>
                                    {item.label}
                                </Typography>
                                <Typography sx={{ color: 'var(--text-neutral-500)' }}>
                                    {item.valueText}
                                </Typography>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Stack>
    );
}

/**
 * 通用销售对比弹窗（折线 + 双饼图 + 图例）。
 */
function SalesCompareDialog({ open, onClose, items, title, lineChartTitle }: SalesCompareDialogProps) {
    const theme = useTheme();
    /** 对齐后的趋势图数据。 */
    const compareTrendData = buildAlignedTrend(items);
    /** 折线图横轴。 */
    const compareCategories = compareTrendData.categories;
    /** 折线图序列。 */
    const compareSeries = compareTrendData.series;
    /** 饼图标签。 */
    const comparePieLabels = items.map((item) => item.label);
    /** 销售量饼图数据。 */
    const compareQuantityPieSeries = items.map((item) => Number(item.total_quantity ?? 0));
    /** 销售额饼图数据。 */
    const compareAmountPieSeries = items.map((item) => Number(item.total_amount ?? 0));
    /** 销售量饼图图例数值。 */
    const compareQuantityLegendValues = items.map((item) => item.quantity);
    /** 销售额饼图图例数值。 */
    const compareAmountLegendValues = items.map((item) => item.amount);
    /** 折线图 Apex options（数据完全由 `items` 派生） */
    const compareLineApexOptions = useMemo(
        () =>
            createDashboardBusinessLineApexOptions(theme, {
                rawXLabels: compareTrendData.categories,
                seriesCount: compareTrendData.series.length,
                height: 392,
            }),
        [theme, items],
    );

    /**
     * 渲染空态。
     * @returns 空态 JSX
     */
    const renderEmpty = () => (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <Typography sx={{ color: CHART_LABEL_COLOR }}>
                {i18n.t('general_text_no_data')}
            </Typography>
        </Box>
    );

    return (
        <DialogComponent
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '640px',
                    maxWidth: 'calc(100vw - 64px)',
                    margin: '32px',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    background: 'var(--fill-neutral-100)',
                    boxShadow: '0px 0px 4px var(--shadow-50) !important',
                    border: 'none',
                    outline: 'none',
                },
            }}
            defaultHeaderProps={{
                titleContent: (
                    <Typography sx={{ color: 'var(--text-primary-300)' }}>
                        {title}
                    </Typography>
                ),
            }}
            maxWidth={false}
            dialogContentStyle={{ paddingTop: 0 }}
            sx={{
                // 覆盖 DialogComponent 默认描边（嵌套选择器优先级高于仅 PaperProps）
                '.MuiDialog-container >.MuiPaper-root': {
                    border: 'none',
                    outline: 'none',
                },
                '.MuiDialogTitle-root': {
                    px: 4,
                    pt: 8,
                    pb: 0,
                    backgroundColor: 'var(--fill-neutral-100)',
                },
                '.MuiDialogContent-root': {
                    px: 4,
                    pb: 4,
                    backgroundColor: 'var(--fill-neutral-100)',
                },
                '.MuiDialogActions-root': {
                    display: 'none',
                },
            }}
        >
            <Stack spacing={4}>
                <Stack spacing={2}>
                    <Typography sx={{ color: 'var(--text-dark)' }}>
                        {lineChartTitle}
                    </Typography>
                    {compareSeries.length > 0 && compareCategories.length > 0 ? (
                        <LinechartOfBusinessData
                            series={compareSeries}
                            apexOptions={compareLineApexOptions}
                            height={392}
                            rootId="line-chart-of-business-data-compare"
                            rawXLabels={compareCategories}
                        />
                    ) : (
                        renderEmpty()
                    )}
                </Stack>
                <ComparePieSection
                    title={i18n.t('business_analysis_page_text_sales_quantity_ratio')}
                    series={compareQuantityPieSeries}
                    labels={comparePieLabels}
                    legendValues={compareQuantityLegendValues}
                    renderEmpty={renderEmpty}
                />
                <ComparePieSection
                    title={i18n.t('business_analysis_page_text_sales_amount_ratio')}
                    series={compareAmountPieSeries}
                    labels={comparePieLabels}
                    legendValues={compareAmountLegendValues}
                    renderEmpty={renderEmpty}
                />
            </Stack>
        </DialogComponent>
    );
}

export default SalesCompareDialog;
